import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Flex } from "../../components/Flex";
import { SubHeader } from "../../components/SubHeader";
import { FormProvider, useForm } from "react-hook-form";
import { GetReportsDto } from "../../model/reports/dto/GetReportsDto";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { useEffect } from "react";
import { reportActions } from "../../redux/report/report.slice";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { Range } from "../../model/shared/dto/GenericWithRange";
import { PageIcons } from "../../utils/GlobalIcons";
import { ReportsSummary } from "./components/ReportsSummary";

export const Reports: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlScope = searchParams.get("scope");
  const initialScope = urlScope?.split(",").filter(Boolean) as
    | [string, string]
    | undefined;

  const initialRange = initialScope?.length
    ? undefined
    : (searchParams.get("range") as Range | null) || Range.THIS_WEEK;

  const formMethods = useForm<GetReportsDto>({
    defaultValues: {
      range: initialRange,
      scope: initialScope?.length === 2 ? initialScope : undefined,
    },
  });
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  useEffect(() => {
    dispatch(
      reportActions.getReport({
        userId,
        ...(initialScope?.length === 2
          ? { scope: initialScope }
          : { range: initialRange }),
      }),
    );

    // Reflects the default into the URL so it's there to restore later, but
    // as a replace: a mount-time URL tweak isn't a page the user visited, and
    // pushing it would cost them a "back" press to get out of Reports at
    // all. Only when landing with neither param - a scope in the URL is a
    // real filter and must not be clobbered back to the default.
    if (!searchParams.get("range") && !searchParams.get("scope")) {
      setSearchParams({ range: Range.THIS_WEEK }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId]);

  return (
    <FormProvider {...formMethods}>
      <Flex flex={1} flexDirection="column">
        <SubHeader title={t("sideMenu.reports")} icon={PageIcons.Reports} />

        <Flex
          flexDirection="column"
          gap="m"
          paddingInline="m"
          paddingBottom="m"
        >
          <ReportsSummary />
        </Flex>
      </Flex>
    </FormProvider>
  );
};
